import './ListItem.scss';

import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import { DefaultAvatar } from 'atoms/default-avatar';
import useWindowDimensions from 'hooks/useWindowDimensions';
import AppointmentActions from 'organisms/Appointments/AppointmentActions';
import { NewClientLabel } from 'organisms/NewClientLabel';
import {
  getAppointmentLastAction,
  isAppointmentCompleted,
} from 'pages/Appointments/util';
import { openDrawer } from 'store/reducers/ui';
import { getRequestStatusLabel, prettifyHour, prettifyPhoneNumber } from 'util';

export default function ListItem({
  item,
  showOutterDate = true,
  showInnerDate,
  showAvatar = true,
  showPetDetails = true,
  showUserNumber = true,
  limitWidth = false,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { width } = useWindowDimensions();
  const isMobile = width <= 600;

  const { note, service, animal, locationUser, status, user, summary } = item;
  const lastAction = getAppointmentLastAction(item);
  const isCompleted = isAppointmentCompleted(item);
  const isRequested = status?.id === 2;
  const userAddress = user?.addresses[0];
  const isMobileService = service?.type?.id === 4;
  const isNewClient = animal?.visits === 1;

  return (
    // We use onMouse events instead of hover + css so actions dropdowns
    // don't remain open when we un-hover an element
    <div
      className={classNames(
        'flex-row list-item align-start',
        limitWidth && 'limit-width'
      )}
    >
      {showOutterDate && (
        <p className="time">{prettifyHour(item.dateStart, true)}</p>
      )}
      <button
        onClick={() => {
          dispatch(openDrawer({ type: 'appointment', uuid: item.uuid }));
        }}
        className={classNames(
          'flex-row list-item-container',
          isRequested && 'status--pending'
        )}
      >
        <div className="flex-row align-center list-item-container-header">
          {showAvatar && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/pet-profile/${animal.uuid}`);
              }}
              className="no-style-btn pointer"
            >
              <DefaultAvatar
                size={isMobile ? 'medium-small' : 'small'}
                animal={animal}
                avatar={animal.links?.image?.desktop?.small}
              />
            </button>
          )}
          <div className="flex-column list-item-container-info">
            {showInnerDate && (
              <p className="f11 bold c-primary-purple">
                {dayjs(item.dateStart).format('D MMMM, hh:mma')}
              </p>
            )}
            <div className="flex-row w-100 justify-between">
              <p className="bold f13">
                {service.name}{' '}
                {summary?.total ? `(${summary.total.localized})` : ''}
              </p>
              {isMobile && isCompleted && (
                <span
                  className={`status-label status-label--${lastAction?.id}`}
                >
                  {getRequestStatusLabel(lastAction?.value)}
                </span>
              )}
            </div>
            <p className="f11 c-primary-purple">
              with <span className="semibold">{locationUser.firstName}</span>
            </p>
            <span className="f11 c-primary-purple">
              {showPetDetails && (
                <div className="flex-row">
                  <div className="flex-row gap-4 align-center">
                    <span className="semibold">{animal.name}</span>
                    {isNewClient && <NewClientLabel />}
                    <span className="semibold"> • </span>
                    <p>
                      {animal.breed?.value && `${animal.breed?.value}`}
                      {animal.breed?.value && animal.hairLength?.value && ' • '}
                      {animal.hairLength?.value &&
                        `${animal.hairLength?.value}`}
                      {animal.hairLength?.value &&
                        animal.vaccinations?.length > 0 &&
                        ' • '}
                      {animal.vaccinations?.length > 0 && 'Vaccinated'}
                    </p>
                  </div>
                </div>
              )}
              {showUserNumber && user?.phone && (
                <p>
                  <span className="semibold">
                    {user?.firstName} {user?.lastName} •{' '}
                  </span>
                  {prettifyPhoneNumber({
                    number: user?.phone.number,
                    countryCode: user?.phone.country.code.value,
                  }) || ''}
                </p>
              )}
              {isMobileService && userAddress && (
                <p>
                  <span className="semibold">Address: </span>
                  {userAddress.address}
                </p>
              )}
              {note && (
                <p>
                  <span className="semibold">Note: </span>
                  {note}
                </p>
              )}
            </span>
          </div>
        </div>
        {!isMobile && (
          <>
            <AppointmentActions appointment={item} type="list" />
            {isCompleted && (
              <span className={`status-label status-label--${lastAction?.id}`}>
                {getRequestStatusLabel(lastAction?.value)}
              </span>
            )}
          </>
        )}
      </button>
    </div>
  );
}

ListItem.propTypes = {
  item: PropTypes.object,
  showAvatar: PropTypes.bool,
  showInnerDate: PropTypes.bool,
  showOutterDate: PropTypes.bool,
  showPetDetails: PropTypes.bool,
  showUserNumber: PropTypes.bool,
};
